import React from "react";
import { View } from "react-native";
import dayjs from "dayjs";
import {
  Link,
  Panel,
  Text,
  theme,
  FlexGrid,
} from "@clearabee/ui-library-native";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { useCatalogue } from "@clearabee/ui-sdk";
import { useTranslation } from "react-i18next";
import { styles } from "./JobView.styles";
import { Image } from "components/Image/Image";
import placeholderImage from "lib/images/placeholder.png";
import { ExtendedJobViewProps } from "./types";
import { showDeliveryText } from "lib/utils";

export const SmallScreenView = ({
  job,
  isLoading,
  address,
  categorySku,
  itemsCollapsed,
  collapseItems,
  isCollapsable,
  showTrain,
}: ExtendedJobViewProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue } = useCatalogue();

  return (
    <>
      {isLoading && (
        <Panel styles={{ padding: 0 }} shadow={false}>
          <SkeletonLoader
            speed={1}
            backgroundColor={theme.colors.light.base}
            foregroundColor={`${theme.colors.greyscale.lighter}00`}
            height={304}
            width="100%"
          >
            <Rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </SkeletonLoader>
        </Panel>
      )}
      {!isLoading && job && (
        <Panel styles={styles.smallPanel} shadow={false}>
          <View style={styles.container}>
            <View>
              <Text
                styles={[styles.heading, { textAlign: "left" }]}
                fontSize="xsmall2"
              >
                {translate(
                  showDeliveryText(categorySku)
                    ? "common.deliveryAddress"
                    : "common.collectionAddress",
                )}
              </Text>
              <Text styles={{ textAlign: "left" }} fontSize="small">
                {address}
              </Text>
            </View>

            <View>
              <Text
                styles={[styles.heading, { textAlign: "right" }]}
                fontSize="xsmall2"
              >
                {translate(
                  showDeliveryText(categorySku)
                    ? "common.deliveryDate"
                    : "common.collectionDate",
                )}
              </Text>
              <Text styles={{ textAlign: "right" }} fontSize="small">
                {dayjs.utc(job?.date).format("ddd DD MMM YYYY")}
              </Text>
              {(job.timePreference || job.timeslotJson) && (
                <>
                  <Text
                    styles={[
                      styles.heading,
                      {
                        textAlign: "right",
                        marginTop: theme.spacing.small,
                      },
                    ]}
                    fontSize="xsmall2"
                  >
                    {translate("common.collectionTime")}
                  </Text>
                  <Text styles={{ textAlign: "right" }} fontSize="small">
                    {job.timePreference === "Any" &&
                      translate(
                        "hwa.screens.dashboard.userDashboard.timeslotPlaceholder",
                      )}
                    {job.timePreference !== "Any" &&
                      job.timePreference !== "" &&
                      job.timePreference}
                    {job.timeslotJson &&
                      `${dayjs(`2020-01-01T${job.timeslotJson.from}`).format(
                        "ha",
                      )} - ${dayjs(`2020-01-01T${job.timeslotJson.to}`).format(
                        "ha",
                      )}`}
                  </Text>
                </>
              )}
              {!showTrain && (
                <>
                  <Text
                    styles={[
                      styles.heading,
                      {
                        textAlign: "right",
                        marginTop: theme.spacing.small,
                      },
                    ]}
                    fontSize="xsmall2"
                  >
                    {translate("common.status")}
                  </Text>
                  <Text styles={{ marginLeft: "auto" }}>{job.status}</Text>
                </>
              )}
            </View>
          </View>
          <FlexGrid rowSpacing={null}>
            <FlexGrid.Cell base="100%" large="66%">
              {job?.items
                ?.slice(0, itemsCollapsed ? 3 : undefined)
                .map((item, index) => (
                  <View
                    key={item.id}
                    style={[styles.product, { borderTopWidth: +!!index }]}
                  >
                    <Image
                      source={
                        defaultCatalogue?.items.find(
                          ({ sku }) => sku === item.sku,
                        )?.img ?? placeholderImage
                      }
                      style={{
                        width: 50,
                        height: 50,
                        alignSelf: "center",
                      }}
                      resizeMode="contain"
                    />
                    <Text
                      fontSize="small"
                      styles={{
                        flexWrap: "wrap",
                        flex: 1,
                        textAlign: "right",
                      }}
                    >
                      {item.title} x {item.qty}
                    </Text>
                  </View>
                ))}

              {isCollapsable && (
                <Link
                  color="brand"
                  styles={{
                    alignSelf: "flex-end",
                  }}
                  textStyles={theme.fontDefaults.small}
                  underline
                  onPress={() => collapseItems(!itemsCollapsed)}
                >
                  {itemsCollapsed
                    ? translate("common.showAll")
                    : translate("common.hideItems")}
                </Link>
              )}
            </FlexGrid.Cell>
          </FlexGrid>
        </Panel>
      )}
    </>
  );
};
